import { useState } from "react";
import { motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import translations from "@/data/translations";
import { Send, CheckCircle, AlertCircle, Loader2, ShieldCheck } from "lucide-react";
import AnimatedSection from "./AnimatedSection";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AuditFormSection = () => {
  const { lang } = useLang();
  const t = translations[lang].auditForm;
  const [form, setForm] = useState({ name: "", company: "", email: "" });
  const [status, setStatus] = useState("idle");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.company || !form.email) return;
    setStatus("loading");
    try {
      const res = await fetch(`${BACKEND_URL}/api/audit-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setStatus("success");
        setForm({ name: "", company: "", email: "" });
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <section data-testid="audit-form-section" className="relative bg-navy py-24 md:py-32 overflow-hidden">
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-electric/10 blur-[100px]" />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-5 md:px-10">
        <AnimatedSection>
          <div className="text-center mb-10">
            <p className="text-xs md:text-sm uppercase tracking-[0.2em] font-semibold text-electric-light mb-4 font-body">
              {t.overline}
            </p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white tracking-tight font-heading mb-4">
              {t.headline}
            </h2>
            <p className="text-lg text-slate-300 font-body max-w-xl mx-auto">{t.subheadline}</p>
          </div>
        </AnimatedSection>

        <AnimatedSection delay={0.2}>
          {status === "success" ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              data-testid="audit-form-success"
              className="bg-white/10 backdrop-blur-lg rounded-2xl p-10 text-center border border-white/10"
            >
              <CheckCircle size={48} className="text-emerald-400 mx-auto mb-4" />
              <p className="text-white text-lg font-semibold font-heading">{t.success}</p>
            </motion.div>
          ) : (
            <form data-testid="audit-form" onSubmit={handleSubmit} className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 md:p-10 border border-white/10">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <input
                  data-testid="audit-form-name" type="text" required value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder={t.namePlaceholder}
                  className="w-full bg-white/10 border border-white/15 rounded-xl px-5 py-3.5 text-white placeholder-slate-400 text-sm font-body focus:outline-none focus:border-electric focus:ring-1 focus:ring-electric transition-colors"
                />
                <input
                  data-testid="audit-form-company" type="text" required value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                  placeholder={t.companyPlaceholder}
                  className="w-full bg-white/10 border border-white/15 rounded-xl px-5 py-3.5 text-white placeholder-slate-400 text-sm font-body focus:outline-none focus:border-electric focus:ring-1 focus:ring-electric transition-colors"
                />
                <input
                  data-testid="audit-form-email" type="email" required value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder={t.emailPlaceholder}
                  className="w-full bg-white/10 border border-white/15 rounded-xl px-5 py-3.5 text-white placeholder-slate-400 text-sm font-body focus:outline-none focus:border-electric focus:ring-1 focus:ring-electric transition-colors"
                />
              </div>
              {status === "error" && (
                <div data-testid="audit-form-error" className="flex items-center gap-2 mb-4 text-red-400 text-sm font-body">
                  <AlertCircle size={16} />{t.error}
                </div>
              )}
              <button
                data-testid="audit-form-submit" type="submit" disabled={status === "loading"}
                className="w-full md:w-auto mx-auto flex items-center justify-center gap-2 bg-electric text-white font-bold py-4 px-10 rounded-full hover:bg-electric-hover transition-all duration-300 shadow-xl shadow-electric/30 hover:shadow-electric/50 text-base disabled:opacity-60"
              >
                {status === "loading" ? <Loader2 size={20} className="animate-spin" /> : <Send size={18} />}
                {t.cta}
              </button>
            </form>
          )}
        </AnimatedSection>

        {/* Trust badges */}
        <AnimatedSection delay={0.4}>
          <div className="flex flex-wrap justify-center gap-6 mt-8">
            {t.trustBadges.map((badge, i) => (
              <div key={i} data-testid={`trust-badge-${i}`} className="flex items-center gap-2">
                <ShieldCheck size={14} className="text-emerald-400" />
                <span className="text-slate-400 text-xs font-medium font-body">{badge}</span>
              </div>
            ))}
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

export default AuditFormSection;
